import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileTextIcon } from '@components/Icons';
import { sendStartOverMessage } from '@utils/station';
import { StationGlowAnimation } from '../StationGlowAnimation/StationGlowAnimation';

interface StationPresentationViewProps {
  presentationUrl: string | null;
  isLoading?: boolean;
  taskId?: string;
}

const USE_IMAGE_BACKGROUND = true;

export const StationPresentationView = ({
  presentationUrl,
  isLoading = false,
}: StationPresentationViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasEverHadUrlRef = useRef(false);
  const lastKnownUrlRef = useRef<string | null>(null);
  const isGeneratingOrRegenerating = isLoading;

  useEffect(() => {
    if (presentationUrl) {
      hasEverHadUrlRef.current = true;
      lastKnownUrlRef.current = presentationUrl;
    }
  }, [presentationUrl]);

  const handleStartOver = useCallback(() => {
    sendStartOverMessage();
    navigate('/');
  }, [navigate]);

  return (
    <div
      className="fixed inset-0 flex flex-col text-white "
      style={{
        background: USE_IMAGE_BACKGROUND
          ? `url('/assets/images/ready.png') center center / cover no-repeat`
          : isGeneratingOrRegenerating
          ? 'linear-gradient(180deg, #7136BE 0%, #7136BE 30%, #D400A1 60%, #D400A1 80%, #000 100%)'
          : 'linear-gradient(180deg, #7136BE 0%, #7136BE 30%, #7136BE 60%, #7136BE 80%, #000 100%)',
      }}>
      {!USE_IMAGE_BACKGROUND && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isGeneratingOrRegenerating
              ? `radial-gradient(900px 900px at 12% 50%, rgba(212,0,161,0.35), transparent 60%),
                 radial-gradient(900px 900px at 88% 50%, rgba(212,0,161,0.35), transparent 60%),
                 radial-gradient(900px 650px at 50% 10%, rgba(212,0,161,0.25), transparent 60%),
                 radial-gradient(900px 700px at 50% 95%, rgba(25,54,104,0.35), transparent 65%)`
              : `radial-gradient(900px 900px at 12% 50%, rgba(113,54,190,0.35), transparent 60%),
                 radial-gradient(900px 900px at 88% 50%, rgba(113,54,190,0.35), transparent 60%),
                 radial-gradient(900px 650px at 50% 10%, rgba(113,54,190,0.25), transparent 60%),
                 radial-gradient(900px 700px at 50% 95%, rgba(25,54,104,0.35), transparent 65%)`,
            filter: 'blur(80px)',
            animation: 'bgFloat 14s ease-in-out infinite',
          }}
        />
      )}
      {!USE_IMAGE_BACKGROUND && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)',
            backdropFilter: 'blur(0px)',
          }}
        />
      )}
      {!USE_IMAGE_BACKGROUND && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 70%, transparent 100%)',
          }}
        />
      )}
      <StationGlowAnimation
        isVisible={USE_IMAGE_BACKGROUND && isGeneratingOrRegenerating}
      />
      <div className="relative z-20 flex flex-col h-full w-full p-10 sm:p-12 md:p-14">
        {(presentationUrl || lastKnownUrlRef.current) && (
          <div className="absolute top-11 right-8 sm:right-12 md:right-16 z-20 flex justify-end items-center">
            <button
              onClick={handleStartOver}
              className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)]">
              {t('generation.startOver')}
            </button>
          </div>
        )}
        <div
          className="relative flex flex-col w-full"
          style={{
            marginTop: '120px',
            height: 'calc(100% - 120px)',
          }}>
          <div
            className="relative flex flex-col w-full  rounded-[36px] p-4 gap-4 flex-1 self-stretch"
            style={{
              background:
                'radial-gradient(ellipse at center, #000 0%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.4) 80%, transparent 100%)',
            }}>
            <div
              className="absolute inset-0 pointer-events-none rounded-[36px] p-[1.567px] z-0"
              style={{
                background:
                  'linear-gradient(to bottom, #666 0%, #666 30%, rgba(102, 102, 102, 0.6) 60%, rgba(102, 102, 102, 0.2) 80%, transparent 100%)',
                mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMask:
                  'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                maskComposite: 'exclude',
                WebkitMaskComposite: 'xor',
              }}
            />
            <div className="relative z-10 flex flex-col w-full h-full rounded-[24px] border-[1.567px] border-white/5 bg-white/5">
              <div className="px-5 py-5 shrink-0 flex items-center gap-3">
                <div className="w-9 h-9 relative overflow-hidden">
                  <FileTextIcon />
                </div>
                <div className="text-white font-['DM_Sans'] text-[26px] font-medium leading-[40px] tracking-[-0.52px]">
                  <span>{t('generation.createPresentation')} </span>
                  <span className="text-white/50">{t('generation.tellMeChanges')}</span>
                </div>
              </div>
              <div className="self-stretch flex-1 min-h-0 ">
                <div
                  className="relative w-full h-full flex flex-col items-start flex-1 self-stretch rounded-[24px] border-[1.567px] border-white/5 bg-white/5"
                  style={{ pointerEvents: 'auto' }}>
                  {isLoading && !hasEverHadUrlRef.current && (
                    <div className="absolute inset-0 pointer-events-none rounded-[24px] overflow-hidden z-10">
                      <div
                        className="absolute inset-0 animate-glass-flow"
                        style={{
                          background:
                            'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)',
                          width: '200%',
                          height: '200%',
                        }}
                      />
                    </div>
                  )}
                  {presentationUrl || lastKnownUrlRef.current ? (
                    <iframe
                      src={
                        presentationUrl || lastKnownUrlRef.current || undefined
                      }
                      className="w-full h-full border-0"
                      style={{ minHeight: '600px', pointerEvents: 'auto' }}
                      title={t('generation.presentation')}
                    />
                  ) : (
                    <div className="flex items-center justify-center py-12 w-full">
                      <p className="text-white/40 text-[15px] leading-[22px] font-light">

                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
